// A member's profile (plan §10, §13): per-speed ratings and a head-to-head
// record against the signed-in viewer.

import {
  api,
  ApiError,
  type GatewaySettings,
  type PlayerProfile,
} from "../api.ts";
import { esc, h, setMsg } from "../dom.ts";
import { navigate, type Params } from "../router.ts";
import { currentUser, setUser } from "../session.ts";
import { boardThemePicker } from "../ui/boardTheme.ts";
import { premoveToggle } from "../game/premove.ts";
import { analysisDepthSelect } from "../engine/analysisDepth.ts";
import { themeSelect } from "../theme.ts";
import { topNav } from "../ui/topnav.ts";
import { quickChallenge, savePrefs } from "../ui/quickChallenge.ts";
import { brandLockup, BRAND_NAME } from "../ui/brand.ts";
import { t } from "../i18n/index.ts";
import { languageSelect } from "../i18n/lang.ts";
import { confirmDialog } from "../ui/confirm.ts";

const SPEED_LABELS: [string, string][] = [
  ["blitz", "chess.blitz"],
  ["rapid", "chess.rapid"],
];

export async function playerScreen(params: Params): Promise<HTMLElement> {
  const user = currentUser();
  if (!user) {
    navigate("/login");
    return h(`<main class="card"><p class="msg">${t("common.redirecting")}</p></main>`);
  }
  const name = params.name ?? "";

  let profile: PlayerProfile;
  try {
    profile = await api.player(name);
  } catch (err) {
    return h(`
      <main class="card">
        <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
        <p class="msg bad">${esc(err instanceof ApiError ? err.message : t("player.loadFailed"))}</p>
        <p><a class="btn ghost" data-link href="/players">${t("player.players")}</a></p>
      </main>`);
  }

  const el = h(`
    <main class="card wide">
      <section class="panel">
        <h2>${esc(profile.username)}</h2>
        <div class="rating-cards"></div>
      </section>
    </main>
  `);
  el.prepend(topNav(null));

  const cards = el.querySelector<HTMLElement>(".rating-cards")!;
  for (const [speed, labelKey] of SPEED_LABELS) {
    const r = profile.ratings[speed];
    const label = t(labelKey);
    const card = document.createElement("div");
    card.className = "rating-card";
    card.innerHTML = r
      ? `<span class="rc-label">${esc(label)}</span><span class="rc-val">${r.rating}${r.provisional ? "?" : ""}</span>`
      : `<span class="rc-label">${esc(label)}</span><span class="rc-val muted">—</span>`;
    cards.append(card);
  }

  // Challenge this member to a game directly (UC-7) — only when the group gate
  // allows it; otherwise explain why the option isn't offered.
  if (!profile.is_self) {
    el.append(profile.can_challenge ? challengePanel(profile.username) : cannotChallengePanel());
  }

  // Head-to-head vs the viewer (server omits it for a self-lookup).
  if (!profile.is_self) {
    const { wins, losses, draws } = profile.h2h;
    const total = wins + losses + draws;
    const panel = h(`
      <section class="panel">
        <h2>${t("player.h2h")}</h2>
        ${
          total === 0
            ? `<p class="fine">${t("player.h2hEmpty", { who: esc(profile.username) })}</p>`
            : `<div class="h2h">
                 <div class="h2h-cell win"><strong>${wins}</strong><span>${t("player.youWon")}</span></div>
                 <div class="h2h-cell draw"><strong>${draws}</strong><span>${t("player.drawn")}</span></div>
                 <div class="h2h-cell loss"><strong>${losses}</strong><span>${t("player.youLost")}</span></div>
               </div>`
        }
      </section>`);
    el.append(panel);
  } else {
    // Own profile → editable private settings (UC-2).
    el.append(settingsPanel());
  }

  return el;
}

/** Challenge another member to a game directly (UC-7). On success the challenger
 *  goes to the waiting room; the target is pinged over the channel ladder. Uses
 *  the shared quick-challenge control (plan §8) and remembers the settings. */
function challengePanel(opponent: string): HTMLElement {
  const panel = h(`
    <section class="panel">
      <h2>${t("player.challengeName", { who: esc(opponent) })}</h2>
      <form id="challenge" novalidate>
        <div class="quick-slot"></div>
        <button class="btn primary" type="submit">${t("player.challengeName", { who: esc(opponent) })}</button>
        <p class="msg" role="status" aria-live="polite"></p>
      </form>
    </section>`);

  const form = panel.querySelector<HTMLFormElement>("#challenge")!;
  const msg = form.querySelector(".msg") as HTMLElement;
  const button = form.querySelector("button") as HTMLButtonElement;
  const qc = quickChallenge();
  panel.querySelector<HTMLElement>(".quick-slot")!.append(qc.element);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const prefs = qc.read();
    button.disabled = true;
    setMsg(msg, t("player.sendingChal"));
    try {
      const res = await api.challengeMember(opponent, prefs.tc, prefs.color, prefs.rated);
      savePrefs(prefs);
      navigate(`/wait/${res.token}`);
    } catch (err) {
      setMsg(msg, err instanceof ApiError ? err.message : t("player.cantChallenge"), "bad");
      button.disabled = false;
    }
  });

  return panel;
}

/** Shown instead of the challenge form when the viewer isn't in any group that
 *  includes this member (the group gate — plan §groups). */
function cannotChallengePanel(): HTMLElement {
  return h(`
    <section class="panel">
      <p class="fine">${t("player.cantReach")}</p>
    </section>`);
}

/** Self-only settings: appearance (theme + board), plus an optional, private
 *  phone number (UC-2). */
function settingsPanel(): HTMLElement {
  const panel = h(`
    <section class="panel">
      <h2>${t("settings.title")}</h2>
      <div class="settings-appearance">
        <div class="appearance-slot"></div>
        <div class="field">
          <span>${t("settings.boardTheme")}</span>
          <div class="board-picker-slot"></div>
        </div>
      </div>
      <div class="settings-gameplay">
        <div class="premove-slot"></div>
        <div class="analysis-depth-slot"></div>
      </div>
      <form id="profile" novalidate>
        <label class="field">
          <span>${t("settings.mobile")}<small class="fine">${t("settings.mobileOpt")}</small></span>
          <input name="phone" type="tel" autocomplete="tel" placeholder="+386 41 123 456" />
        </label>
        <p class="fine">${t("settings.mobileHelp")}</p>
        <button class="btn primary" type="submit">${t("settings.save")}</button>
        <p class="msg" role="status" aria-live="polite"></p>
      </form>
      <div class="gateway-slot"></div>
      <div class="settings-account">
        <button class="btn ghost" id="logout" type="button">${t("settings.signOut")}</button>
      </div>
    </section>`);

  // Appearance controls (theme select + live board swatches) — client-only,
  // persisted in localStorage; no round-trip.
  panel.querySelector<HTMLElement>(".appearance-slot")!.append(themeSelect());
  panel.querySelector<HTMLElement>(".appearance-slot")!.append(languageSelect());
  panel.querySelector<HTMLElement>(".board-picker-slot")!.append(boardThemePicker());
  panel.querySelector<HTMLElement>(".premove-slot")!.append(premoveToggle());
  panel.querySelector<HTMLElement>(".analysis-depth-slot")!.append(analysisDepthSelect());
  void renderGatewaySettings(
    panel.querySelector<HTMLElement>(".gateway-slot")!,
  );

  const form = panel.querySelector<HTMLFormElement>("#profile")!;
  const input = form.querySelector<HTMLInputElement>('input[name="phone"]')!;
  const msg = form.querySelector(".msg") as HTMLElement;
  const button = form.querySelector("button") as HTMLButtonElement;

  // Prefill the current number.
  void (async () => {
    try {
      const me = await api.myProfile();
      if (me.phone) input.value = me.phone;
    } catch {
      /* non-fatal */
    }
  })();

  // Sign out (moved here from the old home topbar — account actions live on the
  // account screen now).
  panel.querySelector<HTMLButtonElement>("#logout")!.addEventListener("click", async () => {
    try {
      await api.logout();
    } catch {
      /* best effort */
    }
    setUser(null);
    navigate("/login");
  });

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    button.disabled = true;
    setMsg(msg, t("settings.saving"));
    try {
      const res = await api.updatePhone(input.value.trim());
      input.value = res.phone ?? "";
      setMsg(msg, res.phone ? t("settings.saved") : t("settings.numberCleared"), "ok");
    } catch (err) {
      setMsg(msg, err instanceof ApiError ? err.message : t("settings.saveFailed"), "bad");
    } finally {
      button.disabled = false;
    }
  });

  return panel;
}

/** "Email it to me" — for the desktop reader whose messenger is on a phone.
 *  The mail carries a Chess link, not a code, so it is safe to sit in an inbox. */
function mailLinkButton(provider: string): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "connect-mail";
  const button = document.createElement("button");
  button.type = "button";
  button.className = "btn ghost sm";
  button.textContent = t("settings.mailLink");
  const note = document.createElement("span");
  note.className = "fine";
  button.onclick = async () => {
    button.disabled = true;
    try {
      const sent = await api.mailGatewayConnectInvite(provider);
      note.textContent = t("settings.mailLinkSent", { address: sent.email });
      button.remove();
    } catch (error) {
      button.disabled = false;
      note.textContent =
        error instanceof ApiError ? error.message : t("settings.channelsFailed");
    }
  };
  wrap.append(button, note);
  return wrap;
}

/** After the deep link opens Telegram the member finishes over there, so poll
 *  briefly and flip the row to "linked" on its own — asking someone to come
 *  back and reload is exactly the friction the one-tap link removes. Bounded by
 *  a deadline and by the slot leaving the DOM. */
function watchForLink(slot: HTMLElement, provider: string): void {
  const deadline = Date.now() + 3 * 60 * 1000;
  const tick = async (): Promise<void> => {
    if (!slot.isConnected || Date.now() > deadline) return;
    try {
      const settings = await api.gatewaySettings();
      const linked = settings.providers.find(
        (candidate) => candidate.provider === provider,
      )?.bindings.length;
      if (linked) {
        void renderGatewaySettings(slot);
        return;
      }
    } catch {
      /* transient — keep polling until the deadline */
    }
    window.setTimeout(() => void tick(), 3000);
  };
  window.setTimeout(() => void tick(), 3000);
}

async function renderGatewaySettings(slot: HTMLElement): Promise<void> {
  slot.innerHTML = `<p class="fine">${t("settings.channelsLoading")}</p>`;
  let settings: GatewaySettings;
  try {
    settings = await api.gatewaySettings();
  } catch (error) {
    slot.innerHTML = `<p class="msg bad">${esc(
      error instanceof ApiError ? error.message : t("settings.channelsFailed"),
    )}</p>`;
    return;
  }

  slot.replaceChildren();
  const wrap = h(`
    <div class="settings-channels">
      <h3>${t("settings.channels")}</h3>
      <p class="fine">${t("settings.channelsHelp")}</p>
      <div class="channel-list"></div>
      <h3>${t("settings.alertOrder")}</h3>
      <p class="fine">${t("settings.alertOrderHelp")}</p>
      <div class="gateway-order"></div>
      <button class="btn ghost save-order" type="button">${t("settings.saveAlerts")}</button>
      <p class="msg gateway-msg" role="status" aria-live="polite"></p>
    </div>`);
  slot.append(wrap);

  const list = wrap.querySelector<HTMLElement>(".channel-list")!;
  for (const provider of settings.providers) {
    const label = providerLabel(provider.provider);
    const binding = provider.bindings[0];
    const virtual = provider.virtual_binding;
    const row = h(`
      <div class="setting-row gateway-channel">
        <div>
          <strong>${esc(label)}</strong>
          <div class="fine channel-state"></div>
        </div>
        <div class="channel-action"></div>
      </div>`);
    const state = row.querySelector<HTMLElement>(".channel-state")!;
    const action = row.querySelector<HTMLElement>(".channel-action")!;
    if (virtual?.verified) {
      state.textContent =
        provider.provider === "email"
          ? t("settings.verifiedAddress", {
              address: virtual.address ?? settings.email.address,
            })
          : t("settings.connected");
    } else if (binding) {
      state.textContent = t("settings.linkedAs", {
        target: binding.display_hint ?? t("settings.verifiedIdentity"),
      });
      const unlink = document.createElement("button");
      unlink.type = "button";
      unlink.className = "btn ghost";
      unlink.textContent = t("settings.unlink");
      unlink.onclick = async () => {
        if (
          !(await confirmDialog(
            t("settings.unlinkConfirm", { provider: label }),
            { danger: true },
          ))
        ) {
          return;
        }
        unlink.disabled = true;
        try {
          await api.unlinkGateway(provider.provider);
          await renderGatewaySettings(slot);
        } catch (error) {
          state.textContent =
            error instanceof ApiError
              ? error.message
              : t("settings.channelsFailed");
          unlink.disabled = false;
        }
      };
      action.append(unlink);
    } else if (provider.configured) {
      state.textContent = t("settings.notLinked");
      const link = document.createElement("button");
      link.type = "button";
      link.className = "btn ghost";
      link.textContent = t("settings.link");
      link.onclick = async () => {
        link.disabled = true;
        try {
          const issued = await api.requestGatewayLink(provider.provider);
          const expires = esc(
            t("settings.codeExpires", {
              time: new Date(issued.expires_at * 1000).toLocaleTimeString(),
            }),
          );
          if (issued.deep_link) {
            // One tap: the deep link carries the code as Telegram's `start`
            // payload, so pressing Start in the chat *is* the link step. The
            // typed command stays as a fallback for anyone without the app on
            // the device they're reading this on.
            state.innerHTML = `
              <a class="btn primary sm gateway-deep-link" target="_blank" rel="noopener noreferrer"
                 href="${esc(issued.deep_link)}">${t("settings.openTelegram")}</a>
              <span class="fine">${t("settings.deepLinkHelp")}</span>
              <span class="fine">${esc(
                t("settings.deepLinkFallback", {
                  command: `/link ${issued.code}`,
                }),
              )}</span>
              <span class="fine">${expires}</span>`;
            // Reading this on a desktop? Mail the button to the phone that
            // actually has the app.
            state.append(mailLinkButton(provider.provider));
            watchForLink(slot, provider.provider);
          } else {
            state.innerHTML = `${esc(issued.instructions)}
              <br><strong class="gateway-code">${esc(issued.code)}</strong>
              <br>${expires}`;
          }
        } catch (error) {
          state.textContent =
            error instanceof ApiError
              ? error.message
              : t("settings.channelsFailed");
        } finally {
          link.disabled = false;
        }
      };
      action.append(link);
    } else {
      state.textContent = t("settings.notAvailable");
    }
    list.append(row);
  }

  const preferenceByProvider = new Map(
    settings.match_preferences.map((preference) => [
      preference.provider,
      preference,
    ]),
  );
  const routeProviders = settings.providers
    .filter(
      (provider) =>
        provider.virtual_binding?.verified || provider.bindings.length > 0,
    )
    .map((provider) => provider.provider);
  routeProviders.sort((a, b) => {
    const aDefault = a === "in_app" ? 0 : a === "email" ? 100 : 50;
    const bDefault = b === "in_app" ? 0 : b === "email" ? 100 : 50;
    return (
      (preferenceByProvider.get(a)?.priority ?? aDefault) -
      (preferenceByProvider.get(b)?.priority ?? bDefault)
    );
  });

  const order = wrap.querySelector<HTMLElement>(".gateway-order")!;
  const renderOrder = () => {
    order.replaceChildren();
    routeProviders.forEach((provider, index) => {
      const row = h(`
        <div class="setting-row gateway-route">
          <label>
            <input type="checkbox" ${
              preferenceByProvider.get(provider)?.enabled === false
                ? ""
                : "checked"
            } />
            <span>${esc(providerLabel(provider))}</span>
          </label>
          <span>
            <button class="btn ghost up" type="button" aria-label="${t("settings.moveUp")}">↑</button>
            <button class="btn ghost down" type="button" aria-label="${t("settings.moveDown")}">↓</button>
          </span>
        </div>`);
      row.dataset.provider = provider;
      row.querySelector<HTMLButtonElement>(".up")!.disabled = index === 0;
      row.querySelector<HTMLButtonElement>(".down")!.disabled =
        index === routeProviders.length - 1;
      row.querySelector<HTMLButtonElement>(".up")!.onclick = () => {
        [routeProviders[index - 1], routeProviders[index]] = [
          routeProviders[index]!,
          routeProviders[index - 1]!,
        ];
        renderOrder();
      };
      row.querySelector<HTMLButtonElement>(".down")!.onclick = () => {
        [routeProviders[index], routeProviders[index + 1]] = [
          routeProviders[index + 1]!,
          routeProviders[index]!,
        ];
        renderOrder();
      };
      order.append(row);
    });
  };
  renderOrder();

  const status = wrap.querySelector<HTMLElement>(".gateway-msg")!;
  const save = wrap.querySelector<HTMLButtonElement>(".save-order")!;
  save.onclick = async () => {
    save.disabled = true;
    const enabled: string[] = [];
    const disabled: string[] = [];
    for (const row of order.querySelectorAll<HTMLElement>(".gateway-route")) {
      const provider = row.dataset.provider!;
      const checked = row.querySelector<HTMLInputElement>("input")!.checked;
      (checked ? enabled : disabled).push(provider);
    }
    try {
      await api.updateMatchPreferences(enabled, disabled);
      setMsg(status, t("settings.alertsSaved"), "ok");
    } catch (error) {
      setMsg(
        status,
        error instanceof ApiError
          ? error.message
          : t("settings.saveFailed"),
        "bad",
      );
    } finally {
      save.disabled = false;
    }
  };
}

function providerLabel(provider: string): string {
  switch (provider) {
    case "in_app":
      return t("settings.providerInApp");
    case "email":
      return t("settings.providerEmail");
    case "telegram":
      return "Telegram";
    case "whatsapp":
      return "WhatsApp";
    case "imessage":
      return "Apple Messages";
    default:
      return provider;
  }
}
